import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gear, Phone, EnvelopeSimple, MapPin, Clock, CalendarPlus } from '@phosphor-icons/react';
import { api } from '../services/api';

const STRUMENTI = ['BATTERIA', 'CANTO', 'CHITARRA', 'CORO', 'PIANOFORTE', 'VIOLINO', 'VIOLONCELLO'];

export default function HomePage() {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [igPosts, setIgPosts] = useState([]);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    api.getCompanySettings().then(res => setCompany(res.data)).catch(() => {});
    api.getInstagramPostsPublic().then(res => setIgPosts(res.data)).catch(() => {});
    const iv = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="min-h-screen bg-[#2D2A4A] flex flex-col relative overflow-hidden" data-testid="home-page">
      {/* Decorative shapes */}
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#E8365D] opacity-80" />
      <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-[#7B68AE] opacity-50" style={{backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.08) 4px, rgba(255,255,255,0.08) 8px)'}} />
      <div className="absolute bottom-10 -left-16 w-40 h-40 rounded-full bg-[#E8365D] opacity-20" />

      <button
        onClick={() => navigate('/login')}
        className="absolute top-5 right-5 p-2 text-white/40 hover:text-white transition-colors z-20"
        data-testid="home-gear-btn"
      >
        <Gear size={24} />
      </button>

      <div className="flex-1 flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16 px-6 py-12 relative z-10">
        {/* Left side - Company info */}
        <div className="max-w-md w-full text-center lg:text-left">
          {company?.logo_url ? (
            <img src={company.logo_url} alt={company?.nome || 'Logo'} className="w-full max-w-[220px] mx-auto lg:mx-0 mb-6" data-testid="home-logo" />
          ) : (
            <div className="w-32 h-32 mx-auto lg:mx-0 mb-6 rounded-full bg-white/10 flex items-center justify-center" data-testid="home-logo-placeholder">
              <span className="text-white/50 text-5xl font-bold font-['Work_Sans']">{(company?.nome || 'FM').charAt(0)}</span>
            </div>
          )}

          <h1 className="text-white text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-4" data-testid="home-company-name">
            {company?.nome || ''}
          </h1>

          {company && (
            <div className="space-y-2.5 text-white/75" data-testid="home-company-info">
              {company.indirizzo && (
                <div className="flex items-center justify-center lg:justify-start gap-2 text-sm">
                  <MapPin size={16} className="text-white/50 shrink-0" />
                  <span>{company.indirizzo}</span>
                </div>
              )}
              {company.telefono && (
                <div className="flex items-center justify-center lg:justify-start gap-2 text-sm">
                  <Phone size={16} className="text-white/50 shrink-0" />
                  <span>Tel. {company.telefono}</span>
                </div>
              )}
              {company.email && (
                <div className="flex items-center justify-center lg:justify-start gap-2 text-sm">
                  <EnvelopeSimple size={16} className="text-white/50 shrink-0" />
                  <span>{company.email}</span>
                </div>
              )}

              <div className="pt-2 mt-2 border-t border-white/10 space-y-1 text-xs text-white/50">
                {company.piva && <p>P.IVA: {company.piva}{company.cf && company.cf !== company.piva ? ` - C.F.: ${company.cf}` : ''}</p>}
                {company.pec && <p>PEC: {company.pec}</p>}
                {company.iban && <p className="font-mono">IBAN: {company.iban}</p>}
              </div>

              {company.segreteria && (
                <div className="flex items-center justify-center lg:justify-start gap-2 text-xs text-white/50 pt-1">
                  <Clock size={14} className="shrink-0" />
                  <span>Segreteria: {company.segreteria}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right side - Instruments banner */}
        <div className="max-w-sm w-full">
          <p className="text-white text-2xl sm:text-3xl mb-5 italic" style={{fontFamily: "'Dancing Script', cursive"}}>
            Lezioni di
          </p>
          <div className="space-y-1.5">
            {STRUMENTI.map((s, i) => (
              <div
                key={s}
                className="text-[#F0D060] text-2xl sm:text-3xl lg:text-4xl font-black tracking-wide animate-fade-in"
                style={{animationDelay: `${i * 80}ms`, fontFamily: "'Work Sans', sans-serif"}}
                data-testid={`strumento-${s.toLowerCase()}`}
              >
                {s}
              </div>
            ))}
          </div>
          <p className="text-white/80 text-xl sm:text-2xl mt-6 italic" style={{fontFamily: "'Dancing Script', cursive"}}>
            e molto altro ancora...
          </p>
        </div>
      </div>

      {/* Booking button - prominent CTA (sempre visibile; disabilitato se data di apertura futura) */}
      {(() => {
        const openAtRaw = company?.public_booking_open_at || '';
        let openAt = null;
        if (openAtRaw) {
          const t = new Date(openAtRaw);
          if (!isNaN(t.getTime())) openAt = t;
        }
        const isFuture = openAt && openAt > now;
        const pad = (n) => String(n).padStart(2, '0');
        const fmtDate = openAt ? `${pad(openAt.getDate())}/${pad(openAt.getMonth() + 1)}/${openAt.getFullYear()}` : '';
        const fmtTime = openAt ? `${pad(openAt.getHours())}.${pad(openAt.getMinutes())}` : '';
        let countdown = '';
        if (isFuture) {
          const diffMs = openAt.getTime() - now.getTime();
          const totalMinutes = Math.floor(diffMs / 60000);
          const days = Math.floor(totalMinutes / (60 * 24));
          const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
          const minutes = totalMinutes % 60;
          const parts = [];
          if (days > 0) parts.push(`${days} ${days === 1 ? 'giorno' : 'giorni'}`);
          if (hours > 0 || days > 0) parts.push(`${hours} ${hours === 1 ? 'ora' : 'ore'}`);
          if (days === 0) parts.push(`${minutes} ${minutes === 1 ? 'minuto' : 'minuti'}`);
          countdown = `Mancano ${parts.join(', ')}`;
        }
        return (
          <div className="flex justify-center pb-14 pt-2 px-6 relative z-10">
            <button
              onClick={() => { if (!isFuture) navigate('/prenota'); }}
              disabled={isFuture}
              className={`group relative inline-flex flex-col items-center gap-1 px-10 py-5 font-black rounded-sm transition-all shadow-2xl tracking-wide ${
                isFuture
                  ? 'bg-[#71717A] text-white cursor-not-allowed normal-case'
                  : 'bg-[#F0D060] text-[#2D2A4A] hover:bg-[#FFE580] hover:shadow-[0_20px_50px_rgba(240,208,96,0.5)] hover:scale-105 animate-pulse-glow cursor-pointer uppercase text-lg sm:text-xl'
              }`}
              data-testid="home-booking-btn"
              style={{fontFamily: "'Work Sans', sans-serif"}}
            >
              {!isFuture && <span className="absolute -inset-1 bg-[#F0D060] rounded-sm opacity-30 blur-lg group-hover:opacity-50 transition-opacity" aria-hidden="true" />}
              {!isFuture ? (
                <span className="flex items-center gap-3 relative">
                  <CalendarPlus size={26} weight="bold" />
                  Prenota uno slot lezioni
                </span>
              ) : (
                <>
                  <span className="flex items-center gap-3 relative text-base sm:text-lg font-bold" data-testid="home-booking-locked-date">
                    <CalendarPlus size={22} weight="bold" />
                    Le iscrizioni apriranno il {fmtDate} alle ore {fmtTime}
                  </span>
                  <span className="text-xs font-semibold tracking-normal opacity-90 mt-0.5" data-testid="home-booking-countdown">
                    {countdown}
                  </span>
                </>
              )}
            </button>
          </div>
        );
      })()}

      {/* Instagram Feed - Instagram profile style */}
      {company?.instagram_enabled && igPosts.length > 0 && (
        <div className="w-full relative z-10 bg-white py-8">
          <div className="max-w-4xl mx-auto px-4">
            {/* Profile header */}
            <div className="flex items-center gap-8 mb-8 pb-6 border-b border-[#DBDBDB]">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden border-2 border-[#DBDBDB] shrink-0 bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] p-[2px]">
                <div className="w-full h-full rounded-full overflow-hidden bg-white p-[2px]">
                  {company?.logo_url ? (
                    <img src={company.logo_url} alt="Profile" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <div className="w-full h-full rounded-full bg-[#FAFAFA] flex items-center justify-center text-2xl font-bold text-[#262626]">FM</div>
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <h2 className="text-xl font-normal text-[#262626]">accademia.fiori.musicali</h2>
                  <a href="https://www.instagram.com/accademia.fiori.musicali/" target="_blank" rel="noopener noreferrer" className="px-4 py-1.5 bg-[#0095F6] text-white text-sm font-semibold rounded-lg hover:bg-[#1877F2] transition-colors">Segui</a>
                </div>
                <div className="flex gap-8 mb-3 text-sm">
                  <span><strong className="font-semibold text-[#262626]">{igPosts.length}</strong> <span className="text-[#8E8E8E]">post</span></span>
                </div>
                <p className="text-sm text-[#262626] font-semibold">{company?.nome || 'Accademia Fiori Musicali'}</p>
                <p className="text-sm text-[#262626]">Scuola di Musica</p>
                {company?.indirizzo && <p className="text-sm text-[#00376B]">{company.indirizzo}</p>}
              </div>
            </div>
            {/* Posts grid - Instagram style */}
            <div className="grid grid-cols-3 gap-1">
              {igPosts.map(p => {
                const match = p.url.match(/instagram\.com\/(?:[^/]+\/)?(?:p|reel)\/([A-Za-z0-9_-]+)/);
                const postId = match ? match[1] : null;
                const isReel = p.url.includes('/reel/');
                const embedType = isReel ? 'reel' : 'p';
                return (
                  <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" className="relative aspect-square overflow-hidden bg-[#FAFAFA] group cursor-pointer block" data-testid={`ig-home-${p.id}`}>
                    {postId ? (
                      <iframe src={`https://www.instagram.com/${embedType}/${postId}/embed/captioned`} className="w-[300%] h-[300%] border-0 origin-top-left scale-[0.334] pointer-events-none" loading="lazy" title={`IG ${postId}`} />
                    ) : null}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center pointer-events-none">
                      <span className="text-white text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity">{p.caption || 'Vedi post'}</span>
                    </div>
                  </a>
                );
              })}
            </div>
            {/* View on Instagram link */}
            <div className="text-center mt-6">
              <a href="https://www.instagram.com/accademia.fiori.musicali/" target="_blank" rel="noopener noreferrer" className="text-[#0095F6] text-sm font-semibold hover:text-[#00376B] transition-colors">
                Vedi tutto su Instagram
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
